import { QueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { CookieKey, LocalStorageKey } from '@common/ts';
import Cookies from 'js-cookie';
import posthog from 'posthog-js';
import { signOutProductlane } from '@/lib/productlane';
import useUserStore from './UserStore';
import Routes from '@/router/routes';

type State = {
  authToken: string | null;
  requireTwoFactor: boolean;
  twoFactorUserId: string | null;
  isLoggingOut: boolean;
};

type Action = {
  setAuthToken: (token: string | null) => void;
  setRequireTwoFactor: (require: boolean) => void;
  setTwoFactorUserId: (userId: string | null) => void;
  clearTwoFactorState: () => void;
  logout: (navigate?: (path: string) => void, queryClient?: QueryClient) => void;
  setIsLoggingOut: (value: boolean) => void;
};

const useAuthStore = create<State & Action>((set) => ({
  authToken: Cookies.get(CookieKey.TOKEN) || null,
  requireTwoFactor: false,
  twoFactorUserId: null,
  isLoggingOut: false,

  setAuthToken: (token) => {
    if (token) {
      Cookies.set(CookieKey.TOKEN, token);
    } else {
      Cookies.remove(CookieKey.TOKEN);
    }
    set({ authToken: token });
  },

  setRequireTwoFactor: (require) => set({ requireTwoFactor: require }),

  setTwoFactorUserId: (userId) => set({ twoFactorUserId: userId }),

  clearTwoFactorState: () => set({ requireTwoFactor: false, twoFactorUserId: null }),

  // TELV1-296 follow-up — `navigate` parameter renamed to `_navigate`
  // because the soft-navigate branch was removed (see the
  // window.location.href call at the bottom of this function for the
  // full rationale). Underscore-prefix is the eslint convention for
  // intentionally-unused function parameters; the type signature still
  // says `navigate` for backward compatibility with callsites.
  logout: (_navigate, queryClient) => {
    const loginPage = Routes.login.path;
    // First set isLoggingOut to true to prevent redirection loops
    set({ isLoggingOut: true });

    // Clear all auth-related cookies
    Cookies.remove(CookieKey.TOKEN);
    Cookies.remove(CookieKey.MAIN_ORGANIZATION);
    Cookies.remove(CookieKey.REMEMBER_ME);
    Cookies.remove(CookieKey.FALLBACK_WORKSPACE);

    // Clear user store
    useUserStore.getState().clearUser();

    // Clear React Query cache to prevent stale user data
    if (queryClient) {
      queryClient.clear();
    }

    // Reset PostHog to clear user identity and prevent identity leak across sessions
    // This ensures the next user's events won't be attributed to the previous user
    try {
      posthog.reset();
    } catch {
      // Analytics failure should not break the application
    }

    // Clear the Productlane support widget session for the same reason
    signOutProductlane();

    // Preserve clientIP when clearing localStoragess
    const clientIP = localStorage.getItem(LocalStorageKey.CLIENT_IP);
    localStorage.clear();
    if (clientIP) {
      localStorage.setItem(LocalStorageKey.CLIENT_IP, clientIP);
    }

    // Clear store state
    set({
      authToken: null,
      requireTwoFactor: false,
      twoFactorUserId: null,
      isLoggingOut: false,
    });

    // TELV1-296 follow-up — always hard-reload via `window.location.href`
    // instead of React Router `navigate()`. The soft-navigate path was
    // introduced in Release 2.30.0 (#1365, 2026-03-03) and turned out to
    // leak in-memory state between user sessions on the same tab:
    //   - Only `useUserStore.clearUser()` is called above; other Zustand
    //     stores (ResultsTabStore, ResponsesPanelStore, CategoryStore,
    //     ErrorStore, etc.) keep their state.
    //   - React Query cache is cleared, but in-flight requests can still
    //     resolve into newly-mounted components after navigation.
    //   - <AuthWrapper> also fires its own `<Navigate to=/login>` when
    //     `authToken` flips to null, racing with this navigate() and
    //     occasionally leaving the user partially navigated.
    // A hard reload is the simplest bulletproof reset: the browser tears
    // the JS context down, App.tsx re-evaluates from scratch with no
    // cookies, and the user lands cleanly on /login.
    // The `navigate` parameter is now unused (renamed to `_navigate`
    // above) but kept on the function signature for backward compatibility
    // with the existing callsite in useUser.ts and to avoid a typescript
    // signature break elsewhere in the codebase.
    window.location.href = loginPage;
  },

  setIsLoggingOut: (value) => set({ isLoggingOut: value }),
}));

export default useAuthStore;
